import { useCallback, useEffect, useRef, useState } from 'react';
import { io, type Socket } from 'socket.io-client';

import { getWsToken } from '../lib/api-client';

export type SessionConnectionState = 'idle' | 'connecting' | 'active' | 'error';

export type MusicDevice = 'driver' | 'follower';
export type MicDevice = 'driver' | 'follower' | 'off';

export interface SessionSettings {
  musicDevice: MusicDevice;
  micDevice: MicDevice;
}

export const DEFAULT_SESSION_SETTINGS: SessionSettings = {
  musicDevice: 'follower',
  micDevice: 'follower',
};

export interface UseSyncSessionReturn {
  sessionId: string | null;
  joinCode: string | null;
  role: 'driver' | 'follower' | null;
  participants: number;
  connectionState: SessionConnectionState;
  error: string | null;
  createSession: (projectId: string) => Promise<void>;
  joinSession: (joinCode: string, externalToken?: string) => Promise<{ projectId: string }>;
  leaveSession: () => void;
  socket: Socket | null;
  sessionSettings: SessionSettings;
  updateSettings: (settings: Partial<SessionSettings>) => void;
}

const SYNC_SERVER_URL =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_SYNC_SERVER_URL) || '';

export function useSyncSession(): UseSyncSessionReturn {
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [joinCode, setJoinCode] = useState<string | null>(null);
  const [role, setRole] = useState<'driver' | 'follower' | null>(null);
  const [participants, setParticipants] = useState(0);
  const [connectionState, setConnectionState] = useState<SessionConnectionState>('idle');
  const [error, setError] = useState<string | null>(null);
  const [sessionSettings, setSessionSettings] = useState<SessionSettings>(DEFAULT_SESSION_SETTINGS);
  const socketRef = useRef<Socket | null>(null);
  const connectionStateRef = useRef(connectionState);
  connectionStateRef.current = connectionState;

  const cleanup = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }
    setSessionId(null);
    setJoinCode(null);
    setRole(null);
    setParticipants(0);
    setConnectionState('idle');
    setError(null);
    setSessionSettings(DEFAULT_SESSION_SETTINGS);
  }, []);

  const connectSocket = useCallback(async (externalToken?: string): Promise<Socket> => {
    const token = externalToken ?? (await getWsToken());
    const socket = io(SYNC_SERVER_URL, {
      auth: { token },
      transports: ['websocket'],
    });
    socketRef.current = socket;

    socket.on('session:participant_joined', (data: { participants: number }) => {
      setParticipants(data.participants);
    });

    socket.on('session:participant_left', (data: { participants: number }) => {
      setParticipants(data.participants);
    });

    socket.on('session:settings', (data: SessionSettings) => {
      setSessionSettings(data);
    });

    socket.on('disconnect', () => {
      if (connectionStateRef.current !== 'idle') {
        setConnectionState('error');
        setError('Connection lost');
      }
    });

    return socket;
  }, []);

  const updateSettings = useCallback((settings: Partial<SessionSettings>) => {
    setSessionSettings((prev) => {
      const next = { ...prev, ...settings };
      socketRef.current?.emit('session:settings', next);
      return next;
    });
  }, []);

  const createSession = useCallback(
    async (projectId: string) => {
      setConnectionState('connecting');
      setError(null);

      try {
        const socket = await connectSocket();

        await new Promise<void>((resolve, reject) => {
          /* v8 ignore next 3 */
          const timeout = setTimeout(() => {
            reject(new Error('Session creation timed out'));
          }, 10000);

          socket.on('connect', () => {
            socket.emit('session:create', { projectId });
          });

          socket.on(
            'session:created',
            (data: { sessionId: string; joinCode: string; participants: number }) => {
              clearTimeout(timeout);
              setSessionId(data.sessionId);
              setJoinCode(data.joinCode);
              setRole('driver');
              setParticipants(data.participants ?? 0);
              setConnectionState('active');
              resolve();
            },
          );

          socket.on('session:error', (data: { code: string; message: string }) => {
            clearTimeout(timeout);
            reject(new Error(data.message));
          });

          socket.on('connect_error', (err: Error) => {
            clearTimeout(timeout);
            reject(err);
          });

          /* v8 ignore next 3 */
          if (socket.connected) {
            socket.emit('session:create', { projectId });
          }
        });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to create session');
        setConnectionState('error');
        if (socketRef.current) {
          socketRef.current.disconnect();
          socketRef.current = null;
        }
      }
    },
    [connectSocket],
  );

  const joinSession = useCallback(
    async (code: string, externalToken?: string): Promise<{ projectId: string }> => {
      setConnectionState('connecting');
      setError(null);

      try {
        const socket = await connectSocket(externalToken);

        return await new Promise<{ projectId: string }>((resolve, reject) => {
          /* v8 ignore next 3 */
          const timeout = setTimeout(() => {
            reject(new Error('Join timed out'));
          }, 10000);

          socket.on('connect', () => {
            socket.emit('session:join', { joinCode: code });
          });

          socket.on(
            'session:joined',
            (data: {
              sessionId: string;
              role: 'follower';
              participants: number;
              currentState: { projectId: string };
            }) => {
              clearTimeout(timeout);
              setSessionId(data.sessionId);
              setRole(data.role);
              setParticipants(data.participants);
              setConnectionState('active');
              resolve({ projectId: data.currentState.projectId });
            },
          );

          socket.on('session:error', (data: { code: string; message: string }) => {
            clearTimeout(timeout);
            reject(new Error(data.message));
          });

          socket.on('connect_error', (err: Error) => {
            clearTimeout(timeout);
            reject(err);
          });

          /* v8 ignore next 3 */
          if (socket.connected) {
            socket.emit('session:join', { joinCode: code });
          }
        });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to join session');
        setConnectionState('error');
        if (socketRef.current) {
          socketRef.current.disconnect();
          socketRef.current = null;
        }
        throw err;
      }
    },
    [connectSocket],
  );

  const leaveSession = useCallback(() => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('session:leave');
    }
    cleanup();
  }, [cleanup]);

  useEffect(() => {
    return () => {
      if (socketRef.current) {
        if (socketRef.current.connected) {
          socketRef.current.emit('session:leave');
        }
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, []);

  return {
    sessionId,
    joinCode,
    role,
    participants,
    connectionState,
    error,
    createSession,
    joinSession,
    leaveSession,
    socket: socketRef.current,
    sessionSettings,
    updateSettings,
  };
}
